import { useState } from "react";
import {
  FieldErrors,
  UseFormRegister,
  Control,
  Controller,
} from "react-hook-form";
import styles from "./index.module.scss";
import classNames from "classnames";
import ErrorMessage from "../Input/ErrorMessage";
import DropdownIcon from "src/assets/images/icon/dropdown_icon.svg";
import CheckIcon from "src/assets/images/icon/check_icon.svg";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface IFormSelectProps {
  label?: string;
  name: string;
  control: Control<any>;
  errors?: FieldErrors<any>;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  required?: boolean;
  defaultValue?: string | number;
  // 성공 메시지 표시
  success?: {
    isSuccess: boolean;
    message: string;
  };
}

const FormSelect = ({
  label,
  name,
  control,
  errors,
  placeholder = "선택하세요",
  options,
  disabled,
  required,
  defaultValue,
  success,
}: IFormSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const error = errors?.[name];
  const isSuccess = success && success.isSuccess;

  return (
    <div className={styles.selectField}>
      {label && (
        <label htmlFor={name} className={styles.selectLabel}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <div className={styles.selectWrapper}>
            <div
              className={classNames(
                styles.selectContainer,
                !!error && styles.invalid,
                isSuccess && styles.valid,
                disabled && styles.disabled,
                isOpen && styles.open
              )}
              onClick={() => !disabled && setIsOpen(!isOpen)}
            >
              <div className={styles.selectDisplay}>
                <span
                  className={classNames(
                    styles.selectValue,
                    !field.value && styles.placeholder
                  )}
                >
                  {field.value
                    ? options.find((option) => option.value === field.value)
                        ?.label
                    : placeholder}
                </span>
                <div
                  className={classNames(
                    styles.selectArrow,
                    isOpen && styles.open
                  )}
                >
                  <img
                    src={DropdownIcon}
                    alt="dropdown arrow"
                    className={classNames(
                      styles.arrowIcon,
                      disabled && styles.disabled
                    )}
                  />
                </div>
              </div>
            </div>

            {isOpen && !disabled && (
              <div className={styles.selectDropdown}>
                <div className={styles.selectOptions}>
                  {options.map((option) => (
                    <div
                      key={option.value}
                      className={classNames(
                        styles.selectOption,
                        option.disabled && styles.optionDisabled,
                        field.value === option.value && styles.optionSelected
                      )}
                      onClick={() => {
                        if (!option.disabled) {
                          field.onChange(option.value);
                          setIsOpen(false);
                        }
                      }}
                    >
                      <div className={styles.optionContent}>
                        {field.value === option.value && (
                          <img
                            src={CheckIcon}
                            alt="selected"
                            className={styles.checkIcon}
                          />
                        )}
                        <span className={styles.optionLabel}>
                          {option.label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 클릭 외부 영역 감지를 위한 오버레이 */}
            {isOpen && (
              <div
                className={styles.selectOverlay}
                onClick={() => setIsOpen(false)}
              />
            )}
          </div>
        )}
      />

      {error && (
        <ErrorMessage type="error" message={error?.message as string} />
      )}
      {isSuccess && <ErrorMessage type="success" message={success.message} />}
    </div>
  );
};

export default FormSelect;
